import React from 'react';
import { Button } from '@roger/ui-components';
import { Loader2, CheckCircle } from 'lucide-react';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type { KinnserOnboardPatientResponse } from '../../hooks/useCreatePatientForm/useKinnserOnboarding';

interface PatientCreateButtonProps {
  handleCreate: () => Promise<void>;
  onComplete?: () => Promise<void>;
  creating: boolean;
  saveStatus: string;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  selectedIntake: { status?: string } | null;
  isUsingOnboarding?: boolean;
  /** When false, button never shows success state (e.g. after "Mark Incomplete") */
  pushToEmrTabComplete?: boolean;
  createResult:
    | KinnserCreatePatientResponse
    | KinnserOnboardPatientResponse
    | RogerOnboardPatientResponse
    | null;
  isValid: boolean;
  pushToRoger?: boolean | null;
  pushToKinnser?: boolean | null;
  /** When true, button is disabled because intake is archived or non-admit */
  pushDisabledByIntakeState?: boolean;
  /** Optional mode override. Defaults to "create" (standard onboarding). */
  mode?: 'create' | 'roc' | 'soc_existing';
  /** When in ROC or soc_existing mode, indicates whether a patient has been explicitly selected from search results. */
  hasSelectedPatient?: boolean;
  /** When true, show "Onboard" label for Kinnser (e.g. new admission/episode auto_add flow). Does not change the handler. */
  preferOnboardLabel?: boolean;
  /** When true with pushToKinnser, success state shows "Patient Scheduled" instead of "Patient Onboarded". */
  isExistingPatient?: boolean;
  /** After a failed push/schedule attempt, keep the action disabled until the user gets support. */
  disableAfterFailure?: boolean;
  className?: string;
}

export const PatientCreateButton: React.FC<PatientCreateButtonProps> = ({
  handleCreate,
  onComplete,
  creating,
  saveStatus,
  loadingClinics,
  loadingClinicData,
  selectedIntake,
  isUsingOnboarding,
  pushToEmrTabComplete = true,
  createResult,
  isValid,
  pushToRoger,
  pushToKinnser,
  pushDisabledByIntakeState = false,
  mode = 'create',
  hasSelectedPatient,
  preferOnboardLabel = false,
  isExistingPatient = false,
  disableAfterFailure = false,
  className,
}) => {
  const isRocMode = mode === 'roc';
  const isSocExistingMode = mode === 'soc_existing';
  const isScheduleOnlyMode = isRocMode || isSocExistingMode;
  const emrName = pushToRoger ? 'Roger' : pushToKinnser ? 'Kinnser' : 'NONE';
  const useOnboardLabel = isUsingOnboarding || preferOnboardLabel;
  const isRogerSuccess =
    pushToRoger &&
    (selectedIntake?.status === 'pushed_to_roger' ||
      (createResult as RogerOnboardPatientResponse)?.success === true);
  const isKinnserSuccess =
    pushToKinnser &&
    (selectedIntake?.status === 'pushed_to_kinnser' ||
      (isUsingOnboarding
        ? (createResult as KinnserOnboardPatientResponse | null)
            ?.patient_creation?.success === true
        : (createResult as KinnserCreatePatientResponse | null)?.success ===
          true));

  const isSocExistingSuccess =
    pushToKinnser &&
    isSocExistingMode &&
    (createResult as { success?: boolean } | null)?.success === true;
  const isSuccess =
    pushToEmrTabComplete &&
    (isRogerSuccess || isKinnserSuccess || isSocExistingSuccess);

  // Status stays PUSHED_TO_* even after "Mark Incomplete"; block re-onboarding to avoid duplicates
  const alreadyOnboardedOnce =
    selectedIntake?.status === 'pushed_to_kinnser' ||
    selectedIntake?.status === 'pushed_to_roger';

  // Button should be disabled if neither EMR is selected or intake is archived/non-admit
  const noEmrSelected = !pushToRoger && !pushToKinnser;

  const baseActionLabel = isSocExistingMode
    ? `Schedule SOC in ${emrName}`
    : isRocMode
      ? `Schedule ROC in ${emrName}`
      : useOnboardLabel
        ? `Onboard in ${emrName}`
        : `Create in ${emrName}`;

  return (
    <Button
      onClick={async () => {
        try {
          await handleCreate();
        } finally {
          // Best-effort refresh so status changes (e.g. push_failed) are reflected
          // even when the action errors.
          if (onComplete) {
            try {
              await onComplete();
            } catch {
              // ignore refresh errors; the original action error (if any) is more important
            }
          }
        }
      }}
      disabled={
        pushDisabledByIntakeState ||
        noEmrSelected ||
        creating ||
        saveStatus === 'saving' ||
        loadingClinics ||
        loadingClinicData ||
        isSuccess ||
        alreadyOnboardedOnce ||
        !isValid ||
        (isScheduleOnlyMode && !hasSelectedPatient) ||
        disableAfterFailure
      }
      className={className}
    >
      {creating ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin mr-2' />
          {isScheduleOnlyMode
            ? 'Scheduling...'
            : useOnboardLabel
              ? 'Onboarding...'
              : 'Creating...'}
        </>
      ) : isSuccess || alreadyOnboardedOnce ? (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {alreadyOnboardedOnce && !isSuccess
            ? 'Patient already onboarded'
            : isSocExistingSuccess
              ? `SOC Scheduled in ${emrName}`
              : isRocMode
                ? `ROC Scheduled in ${emrName}`
                : useOnboardLabel && pushToKinnser && isExistingPatient
                  ? `Patient Scheduled in ${emrName}`
                  : useOnboardLabel
                    ? `Patient Onboarded in ${emrName}`
                    : `Patient Created in ${emrName}`}
        </>
      ) : (
        <>
          <CheckCircle className='h-4 w-4 mr-2' />
          {disableAfterFailure ? 'Push failed' : baseActionLabel}
        </>
      )}
    </Button>
  );
};
